/**
 * The audit table card (TMMAPP-0022; TMMAPP-0103, audit A4 §7, P:1549-1597): Timestamp · Actor ·
 * Event · Entity · Detail · Amount in a card, min-width 920 inside a scroll region, a footnote under
 * it, Load more under the card. Below `md` each row is a card. A row (or its trailing chevron) opens the
 * details (D1; dense rows, TMMAPP-0112): the before / after and the row's Where links. A name the page
 * did not load reads as the type plus Unavailable, never a raw id (MM-D-43). The virtual account's
 * change log uses it too.
 *
 * `layout="list"` is the v2 list (TMMAPP-0151 §J 27, prototype state 44, P:713–722), the `/me/audit`
 * page's at every width: one card of rows, each "{date} · {time}" over the actor at the start, then the
 * entry (the row's sentence, else its Where links, as the table's Detail) over a meta line, the amount at the end, and the
 * same chevron and details. A meeting item's entry is the item's own text (the served `entityLabel`,
 * TMMAPP-0154 §A 6) and its meta line "v{version} · {kind} · {reason}" from the served `ITEM_*` params
 * (TMMAPI-0186 U3), the kind in lower case, whatever of it is served (else the row's sentence); any
 * other row's is its event label and entity. The table stays the virtual account change log's until that tab is restyled.
 */
import { useState, type ReactNode } from 'react';
import { MoneyParts } from '@/components/MoneyText';
import { registerAmountColor } from '@/components/moneyTone';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { AuditEvent } from '@/api/audit';
import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { AiPill } from '@/components/AiPill';
import { CollapseIcon, ExpandIcon } from '@/components/icons';
import { Pill } from '@/components/Pill';
import { formatInstant, formatInstantStamp } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { resolveTokens } from '@/theme/theme';
import { AuditDetails } from './AuditDetails';
import { EVENT_PAINT, eventFamily } from './auditEvents';
import { auditDetailText } from './detailText';
import type { FormatCtx } from './formatAuditValue';
import { humanizeToken } from './labels';

export interface NameMaps {
  virtualAccounts: Readonly<Record<number, string>>;
  accounts: Readonly<Record<number, string>>;
  envelopes: Readonly<Record<number, string>>;
}

export type AuditListMode = 'owner' | 'protected';

interface AuditListProps {
  rows: AuditEvent[];
  nextCursor: string | null;
  loadingMore: boolean;
  onLoadMore: () => void;
  meUserId: number | null;
  mode: AuditListMode;
  /** Virtual account the Activity tab is on — envelope links stay here. */
  currentVirtualAccountId?: number;
  names: NameMaps;
  ctx: FormatCtx;
  /** The prototype's footnote under the table (12px, a top rule). */
  footnote?: ReactNode;
  /** What an empty range says; defaults to the generic copy. */
  emptyText?: ReactNode;
  /** The v1 table (with cards below `md`), or the v2 list at every width. */
  layout?: 'table' | 'list';
}

/** The meeting item actions whose params carry the version and its reason (TMMAPI-0186 U3). */
const ITEM_ACTIONS: ReadonlySet<string> = new Set([
  'ITEM_CREATED',
  'ITEM_CHANGED',
  'ITEM_CONFIRMED',
]);
const ITEM_KINDS: ReadonlySet<string> = new Set(['DECISION', 'COMMITMENT', 'GOAL', 'QUESTION']);

/** "Sep 20" (the year added when it isn't this one) and "09:21", in the person's zone. */
function whenText(t: TFunction, iso: string, ctx: FormatCtx): string {
  try {
    const year = (instant: string) =>
      formatInstant(instant, 'en-CA', ctx.timeZone, { year: 'numeric' });
    const date = formatInstant(
      iso,
      ctx.locale,
      ctx.timeZone,
      year(iso) === year(new Date().toISOString())
        ? { month: 'short', day: 'numeric' }
        : { dateStyle: 'medium' },
    );
    const time = formatInstant(iso, ctx.locale, ctx.timeZone, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    return t('audit.v2.when', { date, time });
  } catch {
    return iso.length > 0 ? iso : '—';
  }
}

/** An item's kind as the meta line says it, in lower case (P:717, TMMAPP-0154 §A 6). */
const KIND_IN_META: Readonly<Record<string, string>> = {
  DECISION: 'audit.v2.kind.decision',
  COMMITMENT: 'audit.v2.kind.commitment',
  GOAL: 'audit.v2.kind.goal',
  QUESTION: 'audit.v2.kind.question',
};

/** A meeting item row's "v{version} · {kind} · {reason}", whatever of it is served; else null. */
function itemMeta(t: TFunction, event: AuditEvent): string | null {
  if (!ITEM_ACTIONS.has(event.action)) return null;
  const params = event.detail?.params ?? {};
  const version = params.version;
  const kind = params.kind;
  const reason = params.reason;
  const kindKey = typeof kind === 'string' && ITEM_KINDS.has(kind) ? KIND_IN_META[kind] : undefined;
  const parts = [
    typeof version === 'number' || (typeof version === 'string' && /^\d+$/.test(version))
      ? t('audit.v2.version', { version })
      : null,
    kindKey === undefined ? null : t(kindKey),
    typeof reason === 'string' && reason.trim().length > 0 ? reason.trim() : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function actionLabel(t: TFunction, action: string): string {
  const key = `audit.action.${action}`;
  const labelled = t(key);
  return labelled === key ? t('audit.action.other', { action: humanizeToken(action) }) : labelled;
}

function entityLabel(t: TFunction, entityType: string): string {
  const key = `audit.entity.${entityType}`;
  const labelled = t(key);
  return labelled === key
    ? t('audit.entity.other', { entity: humanizeToken(entityType) })
    : labelled;
}

/** The non-user actor kinds read as `micro` text, never a chip; the AI actor is the AI pill (TMM-B9). */
function kindLabel(t: TFunction, event: AuditEvent): string | null {
  if (event.actorKind === 'USER' || event.actorKind === 'AI') return null;
  const key = `audit.actor.${event.actorKind}`;
  const labelled = t(key);
  return labelled === key ? humanizeToken(event.actorKind) : labelled;
}

function resolvedEntityName(event: AuditEvent, names: NameMaps): string | null {
  // A `connector_account` event's entity id is the connector row, never a ledger account id
  // (TMMAPP-0097 B6): without a served label it reads "Unavailable", never another account's name.
  if (event.entityType === 'account') return names.accounts[event.entityId] ?? null;
  if (event.entityType === 'envelope') return names.envelopes[event.entityId] ?? null;
  if (event.entityType === 'virtual_account') return names.virtualAccounts[event.entityId] ?? null;
  return null;
}

function servedEntityLabel(event: AuditEvent): string | null {
  return event.entityLabel !== null && event.entityLabel.length > 0 ? event.entityLabel : null;
}

function EntityName({
  event,
  names,
  inline = false,
}: {
  event: AuditEvent;
  names: NameMaps;
  /** Inside a line of text (the v2 meta line), not a cell of its own. */
  inline?: boolean;
}) {
  const { t } = useTranslation();
  const served = servedEntityLabel(event);
  const type = entityLabel(t, event.entityType);
  const text = served ?? resolvedEntityName(event, names) ?? t('audit.entityUnavailable', { type });
  if (inline) return <span>{text}</span>;
  return <Typography sx={{ fontSize: 13 }}>{text}</Typography>;
}

function WhereCell({
  event,
  mode,
  currentVirtualAccountId,
  names,
}: {
  event: AuditEvent;
  mode: AuditListMode;
  currentVirtualAccountId?: number;
  names: NameMaps;
}) {
  const { t } = useTranslation();
  const unavailable = (typeKey: string) => t('audit.nameUnavailable', { type: t(typeKey) });
  const bits: { key: string; node: ReactNode }[] = [];
  const served = servedEntityLabel(event);
  if (event.virtualAccountId !== null) {
    const label =
      event.entityType === 'virtual_account' &&
      event.entityId === event.virtualAccountId &&
      served !== null
        ? served
        : (names.virtualAccounts[event.virtualAccountId] ??
          unavailable('audit.entity.virtual_account'));
    const inside =
      mode === 'owner' ||
      (currentVirtualAccountId !== undefined && event.virtualAccountId === currentVirtualAccountId);
    bits.push({
      key: `d-${event.virtualAccountId}`,
      node: inside ? (
        <Link component={RouterLink} to={`/v/${event.virtualAccountId}`}>
          {label}
        </Link>
      ) : (
        <Typography component="span" variant="inherit">
          {label}
        </Typography>
      ),
    });
  }
  if (event.envelopeId !== null) {
    const label =
      event.entityType === 'envelope' && event.entityId === event.envelopeId && served !== null
        ? served
        : (names.envelopes[event.envelopeId] ?? unavailable('audit.entity.envelope'));
    const linkVirtualAccountId =
      mode === 'protected' && currentVirtualAccountId !== undefined
        ? currentVirtualAccountId
        : event.virtualAccountId;
    bits.push({
      key: `envelope-${event.envelopeId}`,
      node:
        linkVirtualAccountId !== null ? (
          <Link component={RouterLink} to={`/v/${linkVirtualAccountId}/e/${event.envelopeId}`}>
            {label}
          </Link>
        ) : (
          <Typography component="span" variant="inherit">
            {label}
          </Typography>
        ),
    });
  }
  if (event.accountId !== null) {
    const label =
      event.entityType === 'account' && event.entityId === event.accountId && served !== null
        ? served
        : (names.accounts[event.accountId] ?? unavailable('audit.entity.account'));
    bits.push({
      key: `a-${event.accountId}`,
      node:
        mode === 'owner' ? (
          <Link component={RouterLink} to={`/me/accounts/${event.accountId}`}>
            {label}
          </Link>
        ) : (
          <Typography component="span" variant="inherit">
            {label}
          </Typography>
        ),
    });
  }
  if (bits.length === 0) return <>{served ?? '—'}</>;
  return (
    <>
      {bits.map((bit, index) => (
        <Box key={bit.key} component="span">
          {index > 0 ? ' · ' : null}
          {bit.node}
        </Box>
      ))}
    </>
  );
}

interface RowProps {
  event: AuditEvent;
  meUserId: number | null;
  mode: AuditListMode;
  currentVirtualAccountId?: number;
  names: NameMaps;
  ctx: FormatCtx;
  expanded: boolean;
  onToggle: () => void;
}

function useRowParts({ event, meUserId, mode, currentVirtualAccountId, names, ctx }: RowProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  let stamp = '—';
  if (event.occurredAt.length > 0) {
    try {
      stamp = formatInstantStamp(event.occurredAt, ctx.timeZone);
    } catch {
      stamp = event.occurredAt;
    }
  }
  const fromViewer =
    event.actorKind === 'USER' && event.actorUserId !== null && event.actorUserId === meUserId;
  const kind = kindLabel(t, event);
  const actor = (
    <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      {(kind === null && event.actorKind !== 'AI') || event.actorLabel !== null ? (
        <Typography component="span" sx={{ fontSize: 13 }}>
          {fromViewer ? t('audit.actor.USER') : personName(event.actorLabel, t)}
        </Typography>
      ) : null}
      {kind !== null && (
        <Typography component="span" variant="micro" sx={{ color: tokens.textLabel('surface') }}>
          {kind}
        </Typography>
      )}
      {event.actorKind === 'AI' && <AiPill />}
    </Stack>
  );
  const family = eventFamily(event.action);
  const eventPill = (
    <Pill {...EVENT_PAINT[family]} data-paint={family}>
      {actionLabel(t, event.action)}
    </Pill>
  );
  const sentence = auditDetailText(t, event, ctx);
  const where = (
    <WhereCell
      event={event}
      mode={mode}
      {...(currentVirtualAccountId !== undefined ? { currentVirtualAccountId } : {})}
      names={names}
    />
  );
  const amount =
    event.amountMinor === null ? (
      '—'
    ) : (
      <MoneyParts minor={event.amountMinor} currency={event.currency ?? ctx.currency} signed />
    );
  // A list amount: outgoing in ink, incoming `moneyPositive` with "+" (visual contract v2 §13).
  const amountColor =
    event.amountMinor === null ? undefined : registerAmountColor(tokens, event.amountMinor);
  return {
    t,
    tokens,
    stamp,
    actor,
    eventPill,
    sentence,
    where,
    amount,
    amountColor,
    names,
    event,
    ctx,
  };
}

/** The Detail sentence as one block (TMMAPP-0112, the 0103 / 0105 amendment): dense, as drawn. */
function DetailCell({ parts }: { parts: ReturnType<typeof useRowParts> }) {
  const { tokens, sentence, where } = parts;
  return (
    <Typography component="div" sx={{ fontSize: 13, color: tokens.text2 }}>
      {sentence ?? where}
    </Typography>
  );
}

/** The trailing chevron that opens a row's details; the row itself toggles too. */
function DetailsToggle({
  parts,
  expanded,
  onToggle,
}: {
  parts: ReturnType<typeof useRowParts>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t, event } = parts;
  return (
    <IconButton
      size="small"
      aria-expanded={expanded}
      aria-label={t('audit.detailsFor', { event: actionLabel(t, event.action) })}
      onClick={(clicked) => {
        clicked.stopPropagation();
        onToggle();
      }}
    >
      {expanded ? <CollapseIcon /> : <ExpandIcon />}
    </IconButton>
  );
}

function ExpandedBody({ parts }: { parts: ReturnType<typeof useRowParts> }) {
  const { t, tokens, where, event, ctx } = parts;
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: tokens.text2, pt: 1 }}>
        <Box component="span" sx={{ fontWeight: 500 }}>
          {t('audit.whereLabel')}
        </Box>{' '}
        {where}
      </Typography>
      <AuditDetails event={event} ctx={ctx} />
    </Box>
  );
}

function AuditTableRows(props: RowProps) {
  const parts = useRowParts(props);
  const { tokens, stamp, actor, eventPill, amount, amountColor } = parts;
  return (
    <>
      <TableRow
        onClick={props.onToggle}
        data-audit-row=""
        sx={{ cursor: 'pointer', '& > td': { py: '7px' } }}
      >
        <TableCell>
          <Typography
            variant="mono"
            sx={{ fontSize: 12, color: tokens.text2, whiteSpace: 'nowrap' }}
          >
            {stamp}
          </Typography>
        </TableCell>
        <TableCell>{actor}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{eventPill}</TableCell>
        <TableCell>
          <EntityName event={props.event} names={props.names} />
        </TableCell>
        <TableCell>
          <DetailCell parts={parts} />
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            ...(amountColor === undefined ? {} : { color: amountColor }),
          }}
        >
          {amount}
        </TableCell>
        <TableCell sx={{ width: 40, px: 0.5 }}>
          <DetailsToggle parts={parts} expanded={props.expanded} onToggle={props.onToggle} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0, borderBottom: props.expanded ? undefined : 0 }}>
          <Collapse in={props.expanded} unmountOnExit>
            <ExpandedBody parts={parts} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function AuditCard(props: RowProps) {
  const parts = useRowParts(props);
  const { t, tokens, stamp, actor, eventPill, amount, amountColor } = parts;
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack spacing={1}>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="mono" sx={{ fontSize: 12, color: tokens.text2 }}>
            {stamp}
          </Typography>
          <DetailsToggle parts={parts} expanded={props.expanded} onToggle={props.onToggle} />
        </Stack>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {eventPill}
          {actor}
        </Stack>
        <EntityName event={props.event} names={props.names} />
        <DetailCell parts={parts} />
        {props.event.amountMinor !== null && (
          <Typography
            component="div"
            aria-label={t('audit.colAmount')}
            sx={{
              fontSize: 13,
              fontVariantNumeric: 'tabular-nums',
              ...(amountColor === undefined ? {} : { color: amountColor }),
            }}
          >
            {amount}
          </Typography>
        )}
        <Collapse in={props.expanded} unmountOnExit>
          <ExpandedBody parts={parts} />
        </Collapse>
      </Stack>
    </Paper>
  );
}

/** A v2 list row (P:715–718): when and who, then the entry and its meta line. */
function AuditListRow(props: RowProps & { first: boolean }) {
  const parts = useRowParts(props);
  const { t, tokens, sentence, where, amount, amountColor, event, ctx } = parts;
  const label = tokens.textLabel('surface');
  const fromViewer =
    event.actorKind === 'USER' &&
    event.actorUserId !== null &&
    event.actorUserId === props.meUserId;
  const kind = kindLabel(t, event);
  const who = fromViewer
    ? t('audit.actor.USER')
    : event.actorLabel !== null || (kind === null && event.actorKind !== 'AI')
      ? personName(event.actorLabel, t)
      : null;
  // A meeting item's main line is the item's own text, the served `entityLabel` (P:716, TMMAPP-0154
  // §A 6); the generic sentence then falls to the meta line when no "v{n} · {kind} · {reason}" is
  // served. Any other row keeps its sentence over its event and entity.
  const itemText = ITEM_ACTIONS.has(event.action) ? servedEntityLabel(event) : null;
  const main = itemText ?? sentence ?? where;
  const meta = itemMeta(t, event) ?? (itemText !== null && sentence !== null ? sentence : null) ?? (
    <>
      <span data-audit-event="">{actionLabel(t, event.action)}</span>
      {' · '}
      <EntityName event={event} names={props.names} inline />
    </>
  );
  return (
    <Box
      component="li"
      data-audit-row=""
      onClick={props.onToggle}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: '6px 20px',
        p: { xs: '14px 16px', sm: '14px 20px' },
        borderTop: props.first ? 0 : `1px solid ${tokens.line}`,
        cursor: 'pointer',
      }}
    >
      {/* Below sm the amount and the chevron share the first line with when and who. */}
      <Box
        sx={{
          flex: { xs: '1 1 auto', sm: '0 0 150px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <Typography
          component="span"
          data-audit-when=""
          sx={{
            fontSize: 13,
            lineHeight: '19px',
            color: label,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {whenText(t, event.occurredAt, ctx)}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          {who !== null && (
            <Typography
              component="span"
              sx={{ fontSize: 13.5, lineHeight: '19px', fontWeight: 600 }}
            >
              {who}
            </Typography>
          )}
          {kind !== null && (
            <Typography component="span" variant="micro" sx={{ color: label }}>
              {kind}
            </Typography>
          )}
          {event.actorKind === 'AI' && <AiPill />}
        </Stack>
      </Box>
      <Box
        sx={{
          flex: { xs: '1 1 100%', sm: '1 1 300px' },
          order: { xs: 2, sm: 0 },
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
        }}
      >
        <Typography component="span" data-audit-main="" sx={{ fontSize: 14.5, lineHeight: '21px' }}>
          {main}
        </Typography>
        <Typography
          component="span"
          data-audit-meta=""
          sx={{ fontSize: 12.5, lineHeight: '18px', color: label, overflowWrap: 'anywhere' }}
        >
          {meta}
        </Typography>
      </Box>
      {event.amountMinor !== null && (
        <Typography
          component="span"
          aria-label={t('audit.colAmount')}
          sx={{
            fontSize: 14,
            lineHeight: '21px',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            marginInlineStart: 'auto',
            ...(amountColor === undefined ? {} : { color: amountColor }),
          }}
        >
          {amount}
        </Typography>
      )}
      <Box sx={{ marginInlineStart: event.amountMinor === null ? 'auto' : 0, mt: '-4px' }}>
        <DetailsToggle parts={parts} expanded={props.expanded} onToggle={props.onToggle} />
      </Box>
      <Box
        sx={{ flex: '1 1 100%', minWidth: 0, order: { xs: 3, sm: 0 } }}
        onClick={(clicked) => clicked.stopPropagation()}
      >
        <Collapse in={props.expanded} unmountOnExit>
          <ExpandedBody parts={parts} />
        </Collapse>
      </Box>
    </Box>
  );
}

export function AuditList({
  rows,
  nextCursor,
  loadingMore,
  onLoadMore,
  meUserId,
  mode,
  currentVirtualAccountId,
  names,
  ctx,
  footnote,
  emptyText,
  layout = 'table',
}: AuditListProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set());

  function toggle(id: number) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const rowProps = (event: AuditEvent): RowProps => ({
    event,
    meUserId,
    mode,
    ...(currentVirtualAccountId !== undefined ? { currentVirtualAccountId } : {}),
    names,
    ctx,
    expanded: open.has(event.id),
    onToggle: () => toggle(event.id),
  });

  const foot =
    footnote !== undefined && footnote !== null ? (
      <Typography
        sx={{
          p: '14px 20px',
          borderTop: `1px solid ${tokens.line}`,
          fontSize: 12,
          color: tokens.textLabel('surface'),
        }}
      >
        {footnote}
      </Typography>
    ) : null;

  const empty = (
    <Typography
      sx={{ p: '26px 20px', fontSize: 13, color: tokens.text2, maxWidth: 560, lineHeight: 1.6 }}
    >
      {emptyText ?? t('audit.empty')}
    </Typography>
  );

  return (
    <Stack spacing={2}>
      {layout === 'list' ? (
        <Paper component="section" aria-label={t('audit.title')} sx={{ p: 0, overflow: 'hidden' }}>
          {rows.length === 0 ? (
            empty
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {rows.map((event, index) => (
                <AuditListRow key={event.id} {...rowProps(event)} first={index === 0} />
              ))}
            </Box>
          )}
          {foot}
        </Paper>
      ) : md ? (
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer
            tabIndex={0}
            role="region"
            aria-label={t('audit.title')}
            sx={{
              '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: -2 },
            }}
          >
            <Table size="small" aria-label={t('audit.title')} sx={{ minWidth: 920 }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('audit.colTimestamp')}</TableCell>
                  <TableCell>{t('audit.colActor')}</TableCell>
                  <TableCell>{t('audit.colEvent')}</TableCell>
                  <TableCell>{t('audit.colEntity')}</TableCell>
                  <TableCell>{t('audit.colDetail')}</TableCell>
                  <TableCell align="right">{t('audit.colAmount')}</TableCell>
                  <ActionsHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((event) => (
                  <AuditTableRows key={event.id} {...rowProps(event)} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length === 0 && (
            <Typography
              sx={{
                p: '26px 20px',
                fontSize: 13,
                color: tokens.text2,
                maxWidth: 560,
                lineHeight: 1.6,
              }}
            >
              {emptyText ?? t('audit.empty')}
            </Typography>
          )}
          {foot}
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {rows.length === 0 && (
            <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
              {emptyText ?? t('audit.empty')}
            </Typography>
          )}
          {rows.map((event) => (
            <AuditCard key={event.id} {...rowProps(event)} />
          ))}
          {foot}
        </Stack>
      )}
      {nextCursor !== null && (
        <Box>
          <Button variant="outlined" onClick={onLoadMore} disabled={loadingMore}>
            {t('audit.loadMore')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
